import { Component, inject } from '@angular/core';
import { FormBuilder, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { NxButtonModule } from '@aposin/ng-aquila/button';
import { NxFormfieldModule } from '@aposin/ng-aquila/formfield';
import { NxGridModule } from '@aposin/ng-aquila/grid';
import { NxHeadlineModule } from '@aposin/ng-aquila/headline';
import { NxInputModule } from '@aposin/ng-aquila/input';
import { NX_MODAL_DATA, NxModalModule } from '@aposin/ng-aquila/modal';
import { NxSpinnerModule } from '@aposin/ng-aquila/spinner';
import { TranslateModule } from '@ngx-translate/core';

import {
  CompanySearchButtonData,
  CompanySearchResult,
  CompanySearchResultsComponent,
  formatCompany,
} from '#components/company-search-results/company-search-results.component';
import { AdditionalCheckModalData } from '#models/MicroController.model';
import CompanyService, { Company } from '#services/company/company.service';

export interface UltimateParentSearchModalData {
  resultButtonI18n: string;
  titleI18n: string;
}

@Component({
  templateUrl: './ultimate-parent-search-modal.component.html',
  imports: [
    NxHeadlineModule,
    NxFormfieldModule,
    FormsModule,
    NxInputModule,
    ReactiveFormsModule,
    NxButtonModule,
    NxGridModule,
    NxSpinnerModule,
    NxModalModule,
    CompanySearchResultsComponent,
    TranslateModule,
  ],
})
export class UltimateParentSearchModalComponent {
  readonly data = inject<UltimateParentSearchModalData & AdditionalCheckModalData<CompanySearchResult>>(NX_MODAL_DATA);
  private readonly fb = inject(FormBuilder);

  readonly searchForm = this.fb.group({
    ultimateParentEhid: ['', [Validators.required]],
  });
  protected isSearchLoading = false;
  protected hasSearchError = false;
  protected isPerformingAction = false;
  protected searchResult?: CompanySearchResult;

  private readonly companyService: CompanyService = inject(CompanyService);

  protected async onSearch() {
    const ultimateParentEhid = this.searchForm.value.ultimateParentEhid;
    if (ultimateParentEhid) {
      this.searchResult = undefined;
      this.isSearchLoading = true;
      this.hasSearchError = false;
      try {
        const value: Company | undefined = await this.companyService.getCompanyDetails(ultimateParentEhid);
        if (!value) return;
        this.searchResult = formatCompany(value);
      } catch (_) {
        this.hasSearchError = true;
      } finally {
        this.isSearchLoading = false;
      }
    }
  }

  protected onSelectCompany = async () => {
    if (!this.searchResult) return;
    this.isPerformingAction = true;
    this.data.setShouldClose(false);
    try {
      await this.data.performAdditionalCheck(this.searchResult);
    } finally {
      this.isPerformingAction = false;
      this.data.setShouldClose(true);
      this.data.closeModal();
    }
  };

  protected resultButtons: CompanySearchButtonData[][] = [
    [
      {
        i18n: this.data.resultButtonI18n,
        nxButton: 'primary small-medium',
        onSelect: this.onSelectCompany,
        class: 'mr-2.5',
      },
    ],
  ];
}
